/**
 * La frase che dice quando il digest si rigenerera' da solo (F-111).
 *
 * In un modulo senza componenti come `reports/report-format.ts` e
 * `auto-trading/trace-format.ts`: un file che esporta insieme componenti e
 * funzioni rompe il fast refresh, e questa logica si prova meglio da sola —
 * le distinzioni che contano sono tutte nel testo.
 *
 * ## Perche' la frase e' composta e non una costante
 *
 * La generazione automatica e' **una cadenza fissa dentro un cancello mobile**:
 * il job gira ogni pochi minuti, ma il digest schedulato aspetta la prima
 * apertura di un mercato rilevante. Un orario secco sarebbe falso il lunedi',
 * quando il cancello sposta tutto all'apertura, e falso il sabato, quando non
 * si apre affatto.
 *
 * ⚠️ **La meta' piu' importante e' quella sul digest di oggi.** Se esiste gia',
 * il job salta l'utente fino a mezzanotte UTC: la prossima generazione non e'
 * «fra cinque minuti» ma il prossimo giorno di borsa. Senza dirlo, la pagina
 * lascerebbe credere a un automatismo continuo che si e' invece gia' speso.
 *
 * Le **revisioni** di fine giornata non si promettono: nascono solo se la
 * rilettura propone azioni, cioe' da un giudizio su dati che non esistono
 * ancora. Si dice che possono arrivare, non quando.
 */

import { formatDateTime } from '@/lib/format';
import type { NextBriefingRun } from './api';

export function describeNextRun(next: NextBriefingRun): string {
  if (next.blocked === 'feature_disabled') {
    return 'Generazione automatica spenta sul server: il digest si produce solo con «Genera adesso».';
  }
  if (next.blocked === 'no_trigger') {
    return "Nessun trigger di mercato attivo: il digest non si genera da solo. Si accende dalla pagina Mercati, sul mercato che ti interessa.";
  }
  if (!next.at) {
    return 'Nessuna generazione automatica in programma: nessun mercato rilevante apre nelle prossime due settimane.';
  }

  const entro = `entro ${next.pollMinutes} minuti`;
  const quando = formatDateTime(next.at);

  if (!next.sessionDone && !next.waitingFor) {
    return `Il digest di questa seduta non c'è ancora: verrà generato al prossimo giro automatico, ${entro}.`;
  }

  const attesa = next.waitingFor
    ? `all'apertura di ${next.waitingFor.name} (${quando}), ${entro}`
    : `dal ${quando}, ${entro}`;

  if (next.sessionDone) {
    // Le due frasi vanno insieme: la seconda spiega perche' la prima non dice
    // «adesso», e da sola la prima sembrerebbe arbitraria. Si nomina la
    // **seduta** e non il giorno: da F-112 una giornata feriale ne ha due, e
    // dire «il digest di oggi» farebbe sembrare che il pomeriggio non porti
    // nulla.
    return `Prossima generazione automatica ${attesa}. Il digest di questa seduta esiste già; nel frattempo può essere aggiornato da una revisione di fine giornata, se ne produce una con azioni.`;
  }
  return `Prossima generazione automatica ${attesa}.`;
}
